import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  List,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import type { ChangeEvent, FC, MouseEvent } from 'react';
import { useState } from 'react';

import type { SubFilterI } from 'declarations/interfaces/common';

type InsightsDealerGroupSelectPropsT = {
  value: SubFilterI[];
  onChange: (filters: SubFilterI[]) => void;
  onDealerGroupChange?: () => void;
};

/** Dealer Group dropdown for Insights – searchable menu with checkboxes, matches Dealer Card. */
export const InsightsDealerGroupSelect: FC<InsightsDealerGroupSelectPropsT> = ({
  value,
  onChange,
  onDealerGroupChange,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredGroups = value.filter(
    (g): boolean =>
      !searchQuery.trim() || g.label.toLowerCase().includes(searchQuery.trim().toLowerCase())
  );

  const handleToggle = (groupValue: string): void => {
    setMenuAnchor(null);
    setSearchQuery('');
    onChange(
      value.map((g): SubFilterI => (g.value === groupValue ? { ...g, checked: !g.checked } : g))
    );
    onDealerGroupChange?.();
  };

  const count = value.filter((f): boolean => f.checked).length;

  if (value.length === 0) return null;

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        sx={{
          minWidth: 100,
          justifyContent: 'space-between',
          fontSize: 12,
          height: 28,
          textTransform: 'none',
        }}
        endIcon={<ArrowDownwardIcon sx={{ fontSize: 14 }} />}
        onClick={(e: MouseEvent<HTMLButtonElement>): void => {
          setSearchQuery('');
          setMenuAnchor(e.currentTarget);
        }}
      >
        Dealer Groups
        {count > 0 ? (
          <>
            {' '}
            <Box component="span" sx={{ color: 'success.main', fontWeight: 500 }}>
              {`(${count})`}
            </Box>
          </>
        ) : null}
      </Button>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={(): void => {
          setMenuAnchor(null);
          setSearchQuery('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            maxHeight: 380,
            minWidth: 200,
            width: 'max-content',
            maxWidth: 500,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
        MenuListProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            p: 0,
            overflow: 'hidden',
          },
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            p: 1,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <TextField
            size="small"
            placeholder="Search dealer groups"
            value={searchQuery}
            onChange={(e: ChangeEvent<HTMLInputElement>): void => setSearchQuery(e.target.value)}
            variant="outlined"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear dealer group search"
                    size="small"
                    onClick={(): void => setSearchQuery('')}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
        </Box>
        <List dense sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {filteredGroups.map(
            (g): JSX.Element => (
              <MenuItem key={g.value} onClick={(): void => handleToggle(g.value)}>
                <Checkbox checked={g.checked} disableRipple size="small" />
                <ListItemText primary={g.label} sx={{ '& span': { whiteSpace: 'nowrap' } }} />
              </MenuItem>
            )
          )}
          {searchQuery.trim() && filteredGroups.length === 0 && (
            <Typography
              sx={{
                fontSize: 12,
                color: 'text.secondary',
                px: 2,
                py: 1,
              }}
            >
              No dealer groups match your search.
            </Typography>
          )}
        </List>
      </Menu>
    </>
  );
};
